import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
	ChangeDetectorRef,
	OnDestroy,
} from '@angular/core';
import {
	DATE_MDY,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	Owner,
	ProjectDTO,
	RestService,
	TO_DOLLARS,
	TruckSearchTable,
	PROJECT_CARRIER_MAP_STATUS,
	INTEGRATION_TYPE,
} from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { COPY_TO_CLIPBOARD_MSGS, RANGE_FILTERS } from '@domain/constants/app-variables';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { UrlService } from '@service/url/url.service';
import { CarrierToProjectsMap } from '@domain/interface/carrier-to-projects-map';
import { SharedService } from '@service/shared/shared.service';
import { UserCarrierMapHelperService } from '@service/userCarrierMapHelper/user-carrier-map-helper.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { CurrencyPipe } from '@angular/common';
import { environment } from '@environment/environment';
import { IntegrationService } from '@service/integration/integration.service';
import { CarrierFeedbackService } from '@service/carrierFeedback/carrier-feedback.service';
import { ProductsService } from '@service/products/products.service';
import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';
import { SHOW_IN_NETWORK_LOGO, SHOW_ONBOARD_CARRIER_LINK } from '@shared/utils/in-network';
import { truckSearchResult } from 'app/project/store/selectors/recommended-carriers-selectors';
import { HaulpayService } from '@service/haulpay/haulpay.service';
import { BaseComponent } from '@shared/base/base.component';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-pannel-header',
	templateUrl: './pannel-header.component.html',
	styleUrls: ['./pannel-header.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [CurrencyPipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PannelHeaderComponent extends BaseComponent implements OnInit, OnDestroy {
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	card_expanded: boolean = false;

	@Input() searchResult: TruckSearchTable = null;
	@Input() showFMCSA: boolean = false;
	@Input() allProjectsList: Array<ProjectDTO> = null;
	@Input() projectsNextPage: boolean = false;
	@Input() isLoadingProjectsList: boolean = false;
	@Input() totalNumberOfProjects = null;
	@Input() filters = {
		column: '',
		carrierHQ: '',
	};
	@Input() searchTruckForm: null;
	@Input() userCarrierMapList: Array<any> = null;
	@Input() userCarrierMap: any = {};
	@Input() usersList: Array<Owner> = null;
	@Input() allUsersList: Array<Owner> = [];
	@Input() carrierToProjectsMap: Array<CarrierToProjectsMap> = [];
	@Input() isMultiselectEnabled: boolean = false;
	@Input() addCarriersToProject: boolean = false;
	@Input() selectedCarrierIds: Array<string> = [];
	@Input() truckLaneSearchId: string = null;
	@Input() customerCarrier: boolean = undefined;
	@Input() isCarrierVettingIntegrationEnabled: boolean = false;
	@Input() isCarrierOnboarded: boolean = false;
	@Input() CARRIER_QUALITY_ENABLED!: boolean;
	@Input() isExpanded: boolean = false;
	@Input() hideLastColumn: boolean = false; // CN-8312: Hide Lanes/Notes/Projects column for V2/V3 layouts
	@Input() isKanbanView: boolean = false; // CN-8312: Compact card layout for V3 Kanban view

	// CN-8312 V2: when the Assign Rep column renders in V2 row 1, the row needs
	// reduced widths so the total stays at 100% (otherwise it sums to 119% and
	// flexbox falls back to equal distribution, breaking column alignment).
	// Mirrors the *ngIf condition on the Assign Rep <td> so the widths flip
	// in lock-step with the cell's visibility.
	get isAssignRepColumnVisibleInV2(): boolean {
		return (
			this.hideLastColumn &&
			this.productsService.ASSIGN_CARRIERS &&
			!this.utilService.isNullOrUndefined(this.usersList) &&
			this.usersList.length > 0 &&
			!this.utilService.isNullOrUndefined(this.userCarrierMapList)
		);
	}

	@Output() openLaneDetails = new EventEmitter<any>();
	@Output() projectToggled = new EventEmitter<TruckSearchTable>();
	@Output() notesDialogClosed = new EventEmitter<Object>();
	@Output() loadMoreProjects: EventEmitter<any> = new EventEmitter();
	@Output() panelClicked: EventEmitter<any> = new EventEmitter();

	@ViewChild('businessNameContainer') businessNameContainer: ElementRef;
	businessNameMaxWidth;
	DATE_MDY = DATE_MDY;
	COPY_TO_CLIPBOARD_MSGS = COPY_TO_CLIPBOARD_MSGS;
	RANGE_FILTERS = RANGE_FILTERS;
	LANE_TRAILER_TYPE = null;
	carrierSelected: boolean = false;
	environment = environment;

	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;
	TO_DOLLARS = TO_DOLLARS;
	SHOW_IN_NETWORK_LOGO = SHOW_IN_NETWORK_LOGO;
	SHOW_ONBOARD_CARRIER_LINK = SHOW_ONBOARD_CARRIER_LINK;
	isIntegrationLinkAvailable: boolean = false;

	constructor(
		public utilService: UtilService,
		public cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService,
		private urlService: UrlService,
		private restService: RestService,
		private sharedService: SharedService,
		private userCarrierMapHelperService: UserCarrierMapHelperService,
		public accountSettings: AccountSettingsService,
		public integrationService: IntegrationService,
		private carrierFeedbackService: CarrierFeedbackService,
		public productsService: ProductsService,
		public cdRef: ChangeDetectorRef,
		public carrierVettingService: CarrierVettingService,
		public haulpayService: HaulpayService
	) {
		super();
	}

	ngOnInit() {
		if (
			!this.utilService.isNullOrUndefined(this.searchResult.frequency) &&
			this.searchResult.frequency.length > 1
		) {
			const propertyWritable =
				Object.getOwnPropertyDescriptor(this.searchResult, 'frequency') || {};
			if (Boolean(propertyWritable.writable)) {
				this.searchResult.frequency = this.searchResult.frequency.replace(/#|\(|\)/g, '');
			}
		}
		this.setBusinesNameMaxWidth();
		this.LANE_TRAILER_TYPE = this.utilService.getAbbreviatedTrailerTypes(
			this.searchResult.trailerTypes
		);
		this.isIntegrationLinkAvailable = this.integrationService.isIntegrationUrlAvailable();
	}

	isFrequencyValidNumber(frequency): boolean {
		return !/\D/.test(frequency);
	}

	ngOnChanges() {}

	showFullProfile() {
		this.card_expanded = !this.card_expanded;
		const data = {
			lane: this.searchResult,
		};
		this.openLaneDetails.emit(data);
	}

	fireSegmentEvent(projectId: string, carrierId: string) {
		const payload = {
			carrier: {
				businessName: this.searchResult.businessName,
				carrierId: carrierId,
			},
			projectId: projectId,
			page: 'Capacity Search',
		};
		this.segmentWrapper.fireSegmentEvent(
			SegmentEventsConstants.user_added_carrier_to_projects,
			payload
		);
	}

	setBusinesNameMaxWidth() {
		setTimeout(() => {
			if (
				!this.utilService.isNullOrUndefined(this.businessNameContainer) &&
				!this.utilService.isNullOrUndefined(this.businessNameContainer.nativeElement)
			) {
				this.businessNameMaxWidth = this.businessNameContainer.nativeElement.offsetWidth - 26;
			}
		});
	}

	isSearchResultOnlyTruck() {
		return !this.searchResult.isLane && !this.searchResult.isTruckAndLane;
	}

	addUserCarrierMap(user: Owner, carrierId: string) {
		const payload = this.userCarrierMapHelperService.createPayload(user.userId, carrierId, false);
		const url = this.urlService.userCarrierMap();
		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					this.userCarrierMap[carrierId] = user.name;
					this.utilService.showSnackbar('User Assigned.', SNACKBAR.SUCCESS, SNACKBAR.ACTION);
					this.sharedService.sendCarrierAssignedEvent({
						deleted: false,
						carrierId: carrierId,
						assigneeName: user.name,
						userCarrierMap: result.data,
					});
					this.userCarrierMapList = this.userCarrierMapList.filter(
						(cMap: any) => cMap.carrierId != carrierId
					);
					this.userCarrierMapList.push(result.data);
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to set Assignee. Please try again.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	deleteUserCarrierMap(carrierId: string, userCarrierMapId: string) {
		const payload = {
			userCarrierMapIDs: [userCarrierMapId],
		};
		const url = this.urlService.userCarrierMap();
		this.restService
			.DELETE(url, payload)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					delete this.userCarrierMap[carrierId];
					this.utilService.showSnackbar('Assignee removed.', SNACKBAR.SUCCESS, SNACKBAR.ACTION);
					this.sharedService.sendCarrierAssignedEvent({
						deleted: true,
						carrierId: carrierId,
					});
					this.userCarrierMapList = this.userCarrierMapList.filter(
						(cMap: any) => cMap.carrierId != carrierId
					);
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to delete Assignee. Please try again.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	setAssignee(user: Owner, carrierId: string) {
		if (!this.utilService.isNullOrUndefined(user)) {
			const cMap: any = this.userCarrierMapList.filter(
				(cMap: any) => cMap.carrierId == carrierId
			)[0];
			if (
				this.utilService.isNullOrUndefined(cMap) ||
				(!this.utilService.isNullOrUndefined(cMap) && cMap.assigneeId != user.userId)
			) {
				this.addUserCarrierMap(user, carrierId);
			}
		} else if (
			this.utilService.isNullOrUndefined(user) &&
			!this.utilService.isNullOrUndefined(this.userCarrierMap)
		) {
			const userCarrierMapId: string = this.userCarrierMapList.filter(
				(cMap: any) => cMap.carrierId == carrierId
			)[0].id;
			if (!this.utilService.isNullOrUndefined(userCarrierMapId)) {
				this.deleteUserCarrierMap(carrierId, userCarrierMapId);
			}
		}
	}

	getUserIdAssigned(carrierId: string): number {
		return this.userCarrierMapList.filter((cMap: any) => cMap.carrierId == carrierId)[0].assigneeId;
	}

	isCarrierRatesEnabled(): boolean {
		return (
			environment.CARRIER_RATES &&
			this.productsService.CARRIER_RATES &&
			this.accountSettings.CARRIER_RATES
		);
	}

	onboardCarrier() {
		let integrationType = this.integrationService.getEnabledCarrierVettingIntegration();
		this.showMyCarrierEmailConfirmation(integrationType);
	}

	showMyCarrierEmailConfirmation(integrationType: INTEGRATION_TYPE) {
		let payload: any = this.utilService.getUserDataForSegmentEvent();
		payload.search = this.searchResult;
		payload.page = 'capacity_search';
		this.integrationService.showMyCarrierEmailConfirmation(
			this.searchResult.businessName,
			this.searchResult.firmDot,
			this.searchResult.carrierId,
			payload,
			this.searchTruckForm,
			this.truckLaneSearchId,
			integrationType
		);
	}

	async reportDataAction(dataType: string) {
		await this.carrierFeedbackService.reportDataAction(
			dataType,
			this.searchTruckForm,
			this.searchResult
		);
	}

	getCcScoreDisplay(score) {
		return this.sharedService.getCarrierScoreToDisplay(score, this.searchResult.active);
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
